import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useSearch } from "@tanstack/react-router";
import { getPublicStatus } from "@/lib/incidents.functions";
import {
  subscribeToStatus,
  confirmStatusSubscription,
  unsubscribeFromStatus,
} from "@/lib/status-subscribers.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertTriangle, AlertCircle, Activity, Mail } from "lucide-react";

type Search = { tenant?: string; confirm?: string; unsubscribe?: string };

export const Route = createFileRoute("/status")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    tenant: typeof s.tenant === "string" ? s.tenant : undefined,
    confirm: typeof s.confirm === "string" ? s.confirm : undefined,
    unsubscribe: typeof s.unsubscribe === "string" ? s.unsubscribe : undefined,
  }),
  component: PublicStatusPage,
  head: () => ({
    meta: [
      { title: "System Status" },
      { name: "description", content: "Real-time system status and incident history." },
    ],
  }),
});

const STATE_LABEL: Record<string, { label: string; cls: string; icon: any }> = {
  operational: { label: "Operational", cls: "bg-success/15 text-success", icon: CheckCircle2 },
  degraded: { label: "Degraded", cls: "bg-warning/15 text-warning", icon: AlertTriangle },
  partial_outage: { label: "Partial outage", cls: "bg-warning/20 text-warning border border-warning/40", icon: AlertCircle },
  major_outage: { label: "Major outage", cls: "bg-destructive/15 text-destructive", icon: AlertCircle },
};

function PublicStatusPage() {
  const { tenant, confirm, unsubscribe } = useSearch({ from: "/status" });
  const fetchStatus = useServerFn(getPublicStatus);
  const subscribeFn = useServerFn(subscribeToStatus);
  const confirmFn = useServerFn(confirmStatusSubscription);
  const unsubFn = useServerFn(unsubscribeFromStatus);

  const [email, setEmail] = useState("");
  const [flash, setFlash] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  useEffect(() => {
    if (confirm) {
      confirmFn({ data: { token: confirm } })
        .then((r) => setFlash({ kind: "ok", msg: `Subscription confirmed for ${r.email}.` }))
        .catch((e: Error) => setFlash({ kind: "err", msg: e.message }));
    } else if (unsubscribe) {
      unsubFn({ data: { token: unsubscribe } })
        .then((r) => setFlash({ kind: "ok", msg: `Unsubscribed ${r.email}.` }))
        .catch((e: Error) => setFlash({ kind: "err", msg: e.message }));
    }
  }, [confirm, unsubscribe, confirmFn, unsubFn]);

  const subscribe = useMutation({
    mutationFn: async () => {
      if (!tenant) throw new Error("Missing tenant");
      return subscribeFn({
        data: { tenantId: tenant, email, componentSlugs: [], severities: ["minor", "major", "critical"] },
      });
    },
    onSuccess: (r) => {
      setEmail("");
      setFlash({
        kind: "ok",
        msg: r.alreadyConfirmed
          ? "You're already subscribed — preferences updated."
          : "Check your email to confirm the subscription.",
      });
    },
    onError: (e: Error) => setFlash({ kind: "err", msg: e.message }),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-status", tenant],
    enabled: !!tenant,
    refetchInterval: 60_000,
    queryFn: () => fetchStatus({ data: { tenantId: tenant! } }),
  });

  if (!tenant) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-sm text-muted-foreground">
        Provide a <code>?tenant=&lt;uuid&gt;</code> query parameter to view a tenant's status page.
      </div>
    );
  }

  const allOk = (data?.components ?? []).every((c) => c.current_state === "operational");

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">System Status</h1>
        <Badge variant="outline" className="gap-1">
          <Activity className="h-3 w-3" /> Live
        </Badge>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">Failed to load status.</p>}

      {data && (
        <>
          <Card className={allOk ? "border-success/30" : "border-warning/30"}>
            <CardContent className="py-6 flex items-center gap-3">
              {allOk ? (
                <CheckCircle2 className="h-6 w-6 text-success" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-warning" />
              )}
              <div className="text-base font-medium">
                {allOk ? "All systems operational" : "Some systems are experiencing issues"}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Components</CardTitle></CardHeader>
            <CardContent className="divide-y">
              {(data.components ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground py-2">No components configured.</p>
              )}
              {(data.components ?? []).map((c: any) => {
                const meta = STATE_LABEL[c.current_state] ?? STATE_LABEL.operational;
                const Icon = meta.icon;
                return (
                  <div key={c.slug} className="flex items-center justify-between py-2.5">
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      {c.description && (
                        <div className="text-xs text-muted-foreground">{c.description}</div>
                      )}
                    </div>
                    <Badge className={`${meta.cls} text-[11px] gap-1`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </Badge>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Recent Incidents (30d)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(data.incidents ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground">No incidents reported.</p>
              )}
              {(data.incidents ?? []).map((i: any) => (
                <div key={i.id} className="border rounded-md p-3 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm">{i.title}</div>
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-[10px] uppercase">{i.severity}</Badge>
                      <Badge variant="outline" className="text-[10px]">{i.state}</Badge>
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Started {new Date(i.started_at).toLocaleString()}
                    {i.resolved_at && ` · Resolved ${new Date(i.resolved_at).toLocaleString()}`}
                  </div>
                  {(i.updates ?? []).slice(0, 3).map((u: any, idx: number) => (
                    <div key={idx} className="text-xs border-l-2 pl-2 ml-1 mt-1">
                      <span className="font-medium">{u.state}:</span> {u.message}
                      <span className="text-muted-foreground"> · {new Date(u.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="h-4 w-4" /> Get incident notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground">
                We'll email you when an incident is opened, updated, or resolved.
              </p>
              <div className="flex gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="sr-only" htmlFor="status-email">Email</Label>
                  <Input
                    id="status-email"
                    type="email"
                    value={email}
                    placeholder="you@company.com"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <Button
                  onClick={() => subscribe.mutate()}
                  disabled={!email || subscribe.isPending}
                >
                  {subscribe.isPending ? "Subscribing…" : "Subscribe"}
                </Button>
              </div>
              {flash && (
                <div
                  className={`text-xs ${
                    flash.kind === "ok" ? "text-success" : "text-destructive"
                  }`}
                >
                  {flash.msg}
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-[11px] text-muted-foreground text-center">
            Updated {new Date().toLocaleString()} · refreshes every 60s
          </p>
        </>
      )}
    </div>
  );
}